/**
 * SPARC-002: Opportunity pipeline Kanban types
 */

export type PipelineStage = 'new' | 'evaluating' | 'shortlisted' | 'pursuing' | 'won' | 'lost' | 'discarded';

export type BdPriority = 'P1' | 'P2' | 'P3' | 'P4';

export interface PipelineStageHistoryEntry {
  from: PipelineStage | string;
  to: PipelineStage | string;
  changed_by: string;
  changed_at: string;
}

export interface PipelineKanbanColumn {
  id: PipelineStage | 'won_lost' | 'discarded';
  title: string;
  stages: PipelineStage[];
  /** Small dot color (e.g. drag-target affordance elsewhere). */
  color: string;
  /** Background + text classes for the enlarged column header badge. */
  badgeClass: string;
}

// SPARC-ISSUE-013 (Global Opportunities cluster): colors chosen to be clearly
// distinguishable from one another (previously blue-500/cyan-500 and
// slate-500/gray-400 pairs were too close in hue to tell apart at a glance).
// Each entry also carries `badgeClass` (bg + text) so column headers can use a
// filled pill instead of a tiny 12px dot, per the "labels too small" feedback.
export const PIPELINE_KANBAN_COLUMNS: PipelineKanbanColumn[] = [
  {
    id: 'new',
    title: 'New',
    stages: ['new'],
    color: 'bg-slate-500',
    badgeClass: 'bg-slate-100 text-slate-800 dark:bg-slate-700/60 dark:text-slate-100',
  },
  {
    id: 'evaluating',
    title: 'Evaluating',
    stages: ['evaluating'],
    color: 'bg-indigo-600',
    badgeClass: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200',
  },
  {
    id: 'shortlisted',
    title: 'Shortlisted',
    stages: ['shortlisted'],
    color: 'bg-purple-500',
    badgeClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200',
  },
  {
    id: 'pursuing',
    title: 'Pursuing',
    stages: ['pursuing'],
    color: 'bg-orange-500',
    badgeClass: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200',
  },
  {
    id: 'won_lost',
    title: 'Won / Lost',
    stages: ['won', 'lost'],
    color: 'bg-emerald-600',
    badgeClass: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
  },
  {
    id: 'discarded',
    title: 'Discarded',
    stages: ['discarded'],
    color: 'bg-rose-400',
    badgeClass: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200',
  },
];

export const BD_PRIORITY_OPTIONS: BdPriority[] = ['P1', 'P2', 'P3', 'P4'];

export const BD_PRIORITY_LABELS: Record<BdPriority, string> = {
  P1: 'Definitely pursue',
  P2: 'Needs tech validation',
  P3: 'Needs partner',
  P4: 'New geography',
};

export const BD_PRIORITY_BADGE_CLASS: Record<BdPriority, string> = {
  P1: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  P2: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  P3: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  P4: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
};

/** tracking_status values from OpportunitiesDashboard entity schema */
export type TrackingStatus = 'watching' | 'bidding' | 'won' | 'lost' | 'declined';

const VALID_PIPELINE_STAGES: PipelineStage[] = [
  'new',
  'evaluating',
  'shortlisted',
  'pursuing',
  'won',
  'lost',
  'discarded',
];

/**
 * Default Kanban column when pipeline_stage is unset — derived from tracking_status.
 * watching→New; bidding→Pursuing; won/lost→Won/Lost; declined→Discarded.
 */
export const TRACKING_TO_PIPELINE_STAGE: Partial<Record<TrackingStatus, PipelineStage>> = {
  watching: 'new',
  bidding: 'pursuing',
  won: 'won',
  lost: 'lost',
  declined: 'discarded',
};

export function normalizePipelineStage(raw: unknown): PipelineStage {
  const value = String(raw || 'new').trim().toLowerCase();
  return (VALID_PIPELINE_STAGES.includes(value as PipelineStage) ? value : 'new') as PipelineStage;
}

/**
 * Effective Kanban stage: explicit pipeline_stage wins; else map tracking_status;
 * else default to new.
 */
export function resolveEffectivePipelineStage(data: {
  pipeline_stage?: unknown;
  tracking_status?: unknown;
}): PipelineStage {
  const explicit = String(data.pipeline_stage ?? '').trim().toLowerCase();
  if (explicit && VALID_PIPELINE_STAGES.includes(explicit as PipelineStage)) {
    return explicit as PipelineStage;
  }

  const tracking = String(data.tracking_status ?? '').trim().toLowerCase() as TrackingStatus;
  const fromTracking = tracking ? TRACKING_TO_PIPELINE_STAGE[tracking] : undefined;
  if (fromTracking) {
    return fromTracking;
  }

  return 'new';
}

export function columnIdForStage(stage: PipelineStage): PipelineKanbanColumn['id'] {
  if (stage === 'won' || stage === 'lost') return 'won_lost';
  if (stage === 'discarded') return 'discarded';
  return stage;
}

export function defaultStageForColumn(columnId: PipelineKanbanColumn['id']): PipelineStage {
  if (columnId === 'won_lost') return 'won';
  if (columnId === 'discarded') return 'discarded';
  return columnId as PipelineStage;
}

/** True when opportunity belongs in the Discarded Kanban column (stage or tracking_status). */
export function isDiscardedOpportunity(data: {
  pipeline_stage?: unknown;
  tracking_status?: unknown;
}): boolean {
  return resolveEffectivePipelineStage(data) === 'discarded';
}

/** Active pipeline columns (excludes Discarded unless showDiscarded). */
export function visibleKanbanColumns(showDiscarded: boolean): PipelineKanbanColumn[] {
  if (showDiscarded) return PIPELINE_KANBAN_COLUMNS;
  return PIPELINE_KANBAN_COLUMNS.filter((column) => column.id !== 'discarded');
}

/**
 * Default BD priority from tracking_status when bd_priority is not manually set.
 * not_tracking (empty) → none; watching→P4; bidding→P2; won→P1; lost/declined→P4.
 */
export const TRACKING_STATUS_DEFAULT_PRIORITY: Record<TrackingStatus, BdPriority | null> = {
  watching: 'P4',
  bidding: 'P2',
  won: 'P1',
  lost: 'P4',
  declined: 'P4',
};

/**
 * Fallback default from pipeline_stage when tracking_status is empty (Kanban context).
 */
export const PIPELINE_STAGE_DEFAULT_PRIORITY: Record<PipelineStage, BdPriority | null> = {
  new: 'P4',
  evaluating: 'P3',
  shortlisted: 'P3',
  pursuing: 'P2',
  won: 'P1',
  lost: null,
  discarded: null,
};

export function isManualBdPriority(value: unknown): value is BdPriority {
  return typeof value === 'string' && BD_PRIORITY_OPTIONS.includes(value as BdPriority);
}

/**
 * Effective priority: stored manual override, else default from tracking_status,
 * else fallback from pipeline_stage.
 */
export function getEffectiveBdPriority(data: {
  bd_priority?: unknown;
  tracking_status?: unknown;
  pipeline_stage?: unknown;
}): BdPriority | null {
  if (isManualBdPriority(data.bd_priority)) {
    return data.bd_priority;
  }

  const tracking = String(data.tracking_status || '').trim().toLowerCase();
  if (tracking) {
    const mapped = TRACKING_STATUS_DEFAULT_PRIORITY[tracking as TrackingStatus];
    return mapped ?? 'P4';
  }

  const stage = resolveEffectivePipelineStage(data);
  return PIPELINE_STAGE_DEFAULT_PRIORITY[stage];
}

export function isDefaultPriority(data: {
  bd_priority?: unknown;
  tracking_status?: unknown;
  pipeline_stage?: unknown;
}): boolean {
  return !isManualBdPriority(data.bd_priority) && getEffectiveBdPriority(data) !== null;
}

/** Pipeline stage used when discarding from the New column. */
export const DISCARD_PIPELINE_STAGE: PipelineStage = 'discarded';

/** tracking_status values that count as pipeline-tracked without an explicit stage. */
export const PIPELINE_TRACKED_STATUSES: TrackingStatus[] = [
  'watching',
  'bidding',
  'won',
  'lost',
];

/** Cards shown per Kanban column before Load More. */
export const KANBAN_COLUMN_PAGE_SIZE = 20;

/** Server fetch cap for the tracked Kanban query (not the catalog). */
export const KANBAN_TRACKED_FETCH_LIMIT = 200;

/** React Query prefix for the tracked Kanban board (not the catalog dump). */
export const GOVT_OPPORTUNITIES_KANBAN_QUERY_KEY = 'govt-opportunities-kanban';

/** React Query prefix for the full catalog dump used by list/cards stats. */
export const GOVT_OPPORTUNITIES_ALL_QUERY_KEY = 'govt-opportunities-all';

/** React Query prefix for the server-paginated catalog page. */
export const GOVT_OPPORTUNITIES_PAGE_QUERY_KEY = 'govt-opportunities-page';

/**
 * Prefixes the pipeline-stage mutation must patch.
 * Kanban is first: the board reads `govt-opportunities-kanban`, not the catalog dump.
 */
export const PIPELINE_STAGE_CACHE_QUERY_KEYS = [
  GOVT_OPPORTUNITIES_KANBAN_QUERY_KEY,
  GOVT_OPPORTUNITIES_ALL_QUERY_KEY,
  GOVT_OPPORTUNITIES_PAGE_QUERY_KEY,
] as const;

export interface PipelineStageCacheWriter {
  setQueriesData(
    filters: { queryKey: readonly unknown[] },
    updater: (old: unknown) => unknown,
  ): void;
}

export interface PipelineCachedOpportunity {
  id: string;
  data: object;
}

export interface OpportunityPipelineQueryCache {
  entities?: PipelineCachedOpportunity[];
  allEntities?: PipelineCachedOpportunity[];
  [key: string]: unknown;
}

/**
 * Write pipeline_stage onto one cached opportunity list.
 * Used after a successful Kanban drag so the card column can change without a refetch.
 */
export function applyPipelineStageToCachedEntities<T extends PipelineCachedOpportunity>(
  items: T[],
  entityId: string,
  pipelineStage: PipelineStage,
): T[] {
  return items.map((item) =>
    item.id === entityId
      ? { ...item, data: { ...item.data, pipeline_stage: pipelineStage } }
      : item
  );
}

/**
 * Patch Kanban `{ entities }` and catalog `{ allEntities }` cache shapes.
 * The board reads the kanban query; the dump query is a different key and shape.
 */
export function applyPipelineStageToOpportunityQueryData<T extends OpportunityPipelineQueryCache>(
  old: T | undefined | null,
  entityId: string,
  pipelineStage: PipelineStage,
): T | undefined | null {
  if (!old) {
    return old;
  }

  let changed = false;
  const next: T = { ...old };

  if (Array.isArray(old.entities)) {
    next.entities = applyPipelineStageToCachedEntities(old.entities, entityId, pipelineStage);
    changed = true;
  }
  if (Array.isArray(old.allEntities)) {
    next.allEntities = applyPipelineStageToCachedEntities(old.allEntities, entityId, pipelineStage);
    changed = true;
  }

  return changed ? next : old;
}

/**
 * Write pipeline_stage onto every opportunity cache the dashboard keeps live.
 * Must include the Kanban key or a drag toast succeeds while the card stays put.
 */
export function applyPipelineStageToOpportunityCaches(
  queryClient: PipelineStageCacheWriter,
  projectId: string,
  entityId: string,
  pipelineStage: PipelineStage,
): void {
  const applyStage = (old: unknown) =>
    applyPipelineStageToOpportunityQueryData(
      old as OpportunityPipelineQueryCache | undefined,
      entityId,
      pipelineStage,
    );

  for (const prefix of PIPELINE_STAGE_CACHE_QUERY_KEYS) {
    queryClient.setQueriesData({ queryKey: [prefix, projectId] }, applyStage);
  }
}

/**
 * True when the opportunity belongs on the Kanban pipeline: pipeline_stage is set,
 * or tracking_status is watching / bidding / won / lost.
 */
export function isPipelineTracked(data: {
  pipeline_stage?: unknown;
  tracking_status?: unknown;
}): boolean {
  const stage = String(data.pipeline_stage ?? '').trim();
  if (stage) {
    return true;
  }
  const tracking = String(data.tracking_status ?? '').trim().toLowerCase();
  return PIPELINE_TRACKED_STATUSES.includes(tracking as TrackingStatus);
}

/**
 * Resolve match score for Kanban cards: persisted entity data wins, then runtime map.
 */
export function resolveKanbanMatchScore(
  data: { match_score?: unknown },
  runtimeScore?: number
): number | null {
  if (typeof data.match_score === 'number' && !Number.isNaN(data.match_score)) {
    return data.match_score;
  }
  if (runtimeScore !== undefined && !Number.isNaN(runtimeScore)) {
    return runtimeScore;
  }
  return null;
}
